import { useMutation, useQueryClient } from '@tanstack/react-query';
import { insights } from '@repletoryreach/sdk-api';
import { Button, Card, Group, Progress, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { Check } from '@repletoryreach/sdk-ui/icons';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useI18n } from '../../i18n/I18nProvider';
import { formatCount } from '../../lib/format';
import { INSIGHTS_KEY, STAGES, insightErrorText } from './lib/data';
import { MUTED, depthLabel, periodLabel } from './shared';

/**
 * How far a report has got overall, 0 to 100: counting is quick, reading takes most of the time (by chats read),
 * then sorting and the two writing calls.
 */
export function overallProgress(report: insights.InsightReportSummary): number {
  if (report.status === 'queued') return 0;
  const part = report.progress_total > 0 ? Math.min(1, report.progress_done / report.progress_total) : 0;
  switch (report.stage) {
    case 'gather': return 3;
    case 'read': return Math.round(5 + 75 * part);
    case 'organise': return 82;
    case 'write': return Math.round(88 + 10 * part);
    default: return 100;
  }
}

/**
 * A report on its way: the four stages as a stepper (a tick for each one finished, a ring around the one it is on), one
 * bar for how far it has got overall, what it is doing now, and Cancel. The business can leave the page; the email and
 * the notification say when it is ready.
 */
export function RunningCard({ report }: { report: insights.InsightReportSummary }) {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const cancel = useMutation({
    mutationFn: () => insights.cancelInsightReport(report.id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: [INSIGHTS_KEY] }); notifySuccess(t('insights.running.cancelled'), t('insights.title')); },
    onError: (e) => notifyError(insightErrorText(t, e), t('insights.title'))
  });
  const current = report.status === 'queued' ? -1 : STAGES.indexOf(report.stage);
  const overall = overallProgress(report);

  return (
    <Card withBorder className="rr-insights__running" data-testid="insights-running">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" gap="sm" wrap="wrap">
          <Stack gap={2} style={{ minWidth: 0, flex: '1 1 240px' }}>
            <Title order={4}>{t('insights.running.title')}</Title>
            <Text role="caption" c={MUTED}>
              {periodLabel(t, report.from, report.to, locale)} · {depthLabel(t, report.depth)}
            </Text>
          </Stack>
          <Button size="xs" variant="default" loading={cancel.isPending} data-testid="insights-cancel"
            onClick={() => openConfirmModal({
              title: t('insights.running.cancelTitle'),
              children: <Text role="bodySmall">{t('insights.running.cancelBody')}</Text>,
              labels: { confirm: t('insights.running.cancel'), cancel: t('action.back') },
              onConfirm: () => cancel.mutate()
            })}>
            {t('insights.running.cancel')}
          </Button>
        </Group>
        <ol className="rr-insights__stages" aria-label={t('insights.running.stages')}>
          {STAGES.map((s, i) => {
            const state = i < current ? 'done' : i === current ? 'now' : 'later';
            return (
              <li key={s} className={`rr-insights__stage is-${state}`} aria-current={state === 'now' ? 'step' : undefined}>
                <span className="rr-insights__stage-dot" aria-hidden>{state === 'done' ? <Check size={14} /> : i + 1}</span>
                <span className="rr-insights__stage-text">
                  <span className="rr-insights__stage-name">{t(`insights.stage.${s}`)}</span>
                  <span className="rr-insights__stage-note">
                    {state === 'now' && s === 'read' && report.progress_total > 0
                      ? t('insights.stage.of', { done: formatCount(report.progress_done), total: formatCount(report.progress_total) })
                      : t(`insights.stage.${state}`)}
                  </span>
                </span>
              </li>
            );
          })}
        </ol>
        <Stack gap={6}>
          <Group justify="space-between" gap="sm" wrap="nowrap">
            <Text role="bodySmall" data-testid="insights-progress">
              {report.status === 'queued' ? t('insights.running.queued')
                : report.stage === 'read' && report.progress_total > 0
                  ? t('insights.running.reading', { done: formatCount(report.progress_done), total: formatCount(report.progress_total) })
                  : t(`insights.running.${report.stage}`)}
            </Text>
            <Text role="caption" c={MUTED} style={{ whiteSpace: 'nowrap' }}>{t('insights.running.overall', { pct: overall })}</Text>
          </Group>
          <Progress value={overall} size="sm" striped animated={report.status === 'running'} aria-label={t('insights.running.stages')} />
        </Stack>
        <Text role="caption" c={MUTED}>{t('insights.running.leave')}</Text>
      </Stack>
    </Card>
  );
}
